import { useState } from 'react';
import { useSweepNotifications } from '../../../../notifications/useSweepNotifications';
import { SweepNotificationVariant } from '../../../../../reducers/notificationsReducer';
import { telemetry } from '../../../../../telemetry';
import { useSelector } from 'react-redux';
import { Box } from '@mui/material';
import { CenteredCircularProgress } from '../../../../common/CenteredCircularProgress';
import { DependenciesSideDialogContent } from '../../../../documentation/salesforce-documentation-card/DependenciesSideDialogContent';
import { DocumentationCardContentRenderer } from '../../../../documentation/salesforce-documentation-card/DocumentationCardContentRenderer';
import { DocumentationCardContentViewType } from '../../../../documentation/salesforce-documentation-card/types';
import { useRunOnceWhenTruthy } from '../../../../common/useRunOnceWhenTruthy';
import { useSalesforceCard } from '../../../../documentation/salesforce-documentation-card/hooks/useSalesforceCard';
import { selectDocumentationCardItem } from '../../../../../reducers/documentation/documentationCardReducer';
import { colors } from '@sweep-io/sweep-design';
import { UniversalSearchProvider } from '../../../../documentation/universal-search/UniversalSearchContext';
import useParser from '../../../../parser/useParser';
import { AddToCanvasButton } from '../metadata-elements/AddToCanvasButton';
import { isMetadataElementInCanvas } from '../metadata-elements/helper';
import { slideFromLeft } from '../../../../animations/cssAnimations';
import { DocumentationFiltersProvider } from '../../../../documentation/selected-object/filters/DocumentationFiltersContext';
import { useFetchConfigurationItem } from './useFetchConfigurationItem';
import { UI_EVENTS } from '../../../../../services/events';
import { OBJECT } from '@server/parser';
import { isCustomObjectByName } from '../../../../documentation/cpq/utils';
import { getConfigurationItemColor } from '../../../../documentation/helpers';
import { selectTagsList } from '../../../../../reducers/documentation/userInputsReducer';
import { DocumentationCardShell } from '../../../../documentation/building-blocks/documentation-card/DocumentationCardShell';

interface DependenciesPanelProps {
  metadataElementId: string;
  crmOrgId: string;
  onClose: () => void;
  color?: string;
  canvasFunnelMap: FunnelMap;
}

const DependenciesPanel = ({
  metadataElementId,
  crmOrgId,
  onClose,
  color,
  canvasFunnelMap,
}: DependenciesPanelProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const { addNotification } = useSweepNotifications();
  const { handleSetConfigurationItem } = useSalesforceCard(crmOrgId);
  const selectedCardItem = useSelector(selectDocumentationCardItem);
  const configurationItem =
    selectedCardItem?.platform === 'salesforce' ? selectedCardItem.item : undefined;
  const { fetchConfigurationItem } = useFetchConfigurationItem();
  const { parseObjectIfNotParsed } = useParser({ crmOrgId });
  const _configurationItem = configurationItem ? { ...configurationItem } : undefined; //make configurationItem mutable
  const tags = useSelector(selectTagsList);

  useRunOnceWhenTruthy(async () => {
    setIsLoading(true);

    try {
      const item = await fetchConfigurationItem(metadataElementId, crmOrgId);
      if (item.parentType === OBJECT) {
        await parseObjectIfNotParsed(item.name);
      }

      handleSetConfigurationItem({
        item,
        contentType: DocumentationCardContentViewType.default,
        clearHistory: true,
      });
    } catch (err) {
      telemetry.captureError(err);
      addNotification({
        variant: SweepNotificationVariant.Error,
        message: 'Failed to load metadata element',
      });
      onClose();
    } finally {
      setIsLoading(false);
    }
  }, !!metadataElementId);

  const type = _configurationItem?.parentType;
  const showLoader = isLoading || !_configurationItem || !type;
  const isInCanvas = isMetadataElementInCanvas({
    crmOrgId,
    metadataId: _configurationItem?.metadataId ?? '',
    canvasFunnelMap,
  });

  const objectName = _configurationItem?.objectApiNames?.[0];
  const isPaywalled = !!objectName && isCustomObjectByName(objectName);
  const resolvedColor = _configurationItem
    ? getConfigurationItemColor(_configurationItem, color, tags)
    : undefined;

  return (
    <Box
      sx={{
        position: 'absolute' as const,
        left: 0,
        top: 0,
        borderRadius: 0,
        bottom: 0,
        height: '100%',
        boxShadow: 1,
        width: '464px',
        backgroundColor: colors.white,
        zIndex: 101, //to be on top of the layer selector
        ...slideFromLeft,
      }}
    >
      {showLoader && (
        <DocumentationCardShell onClose={onClose}>
          <CenteredCircularProgress />
        </DocumentationCardShell>
      )}
      {!showLoader && (
        <UniversalSearchProvider crmOrgId={crmOrgId}>
          <DependenciesSideDialogContent
            crmOrgId={crmOrgId}
            item={_configurationItem}
            type={type}
            onClose={onClose}
            isReadOnly={true}
            color={resolvedColor}
            isPaywalled={isPaywalled}
            paywallObjectName={objectName}
            headerContent={
              <AddToCanvasButton
                isStaticView={true}
                isInCanvas={isInCanvas}
                element={_configurationItem}
                callback={onClose}
                crmOrgId={crmOrgId}
                biEventName={UI_EVENTS.canvasAddMetadataToCanvas}
              />
            }
          >
            <DocumentationCardContentRenderer
              crmOrgId={crmOrgId}
              configurationItem={_configurationItem}
              type={type}
              isReadOnly={true}
            />
          </DependenciesSideDialogContent>
        </UniversalSearchProvider>
      )}
    </Box>
  );
};

const DependenciesPanelWithContext = (props: DependenciesPanelProps) => (
  <DocumentationFiltersProvider>
    <DependenciesPanel {...props} />
  </DocumentationFiltersProvider>
);

export { DependenciesPanelWithContext as DependenciesPanel };
